import { useEffect, useState } from "react";
import { propertyService } from "@/features/properties/model/propertyServices";
import { toastService } from "@/shared/toast";
import { Modal } from "@/shared/modal";
import styles from "./ServiceExpansionRequestModal.module.css";

export type ServiceExpansionDefaults = {
  name: string;
  email: string;
  phone: string;
  state: string;
  city: string;
};

type ServiceExpansionRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  defaults: ServiceExpansionDefaults;
};

const PersonIcon = () => (
  <svg className={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4.42 0-8 1.79-8 4v2h16v-2c0-2.21-3.58-4-8-4Z"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg className={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.07 21 3 13.93 3 5a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.58a1 1 0 0 1-.24 1.01l-2.2 2.2Z"
    />
  </svg>
);

const MailIcon = () => (
  <svg className={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5L4 8V6l8 5 8-5v2Z"
    />
  </svg>
);

const PinIcon = () => (
  <svg className={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z"
    />
  </svg>
);

const BuildingIcon = () => (
  <svg className={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 3 2 9v12h7v-7h6v7h7V9l-10-6Zm0 2.84 6 3.6V19h-1v-6H7v6H6v-9.56l6-3.6Z"
    />
  </svg>
);

const MapPinHero = () => (
  <svg width="72" height="88" viewBox="0 0 64 80" aria-hidden="true">
    <path
      fill="#22c55e"
      d="M32 0C17.64 0 6 11.64 6 26c0 15.75 26 54 26 54s26-38.25 26-54C58 11.64 46.36 0 32 0Z"
    />
    <circle cx="32" cy="26" r="14" fill="#ecfdf5" />
    <path fill="#16a34a" d="M22 32h20v2H22v-2Zm2-14h5v12h-5V18Zm7 0h5v8h-5v-8Z" />
  </svg>
);

export function ServiceExpansionRequestModal({
  isOpen,
  onClose,
  defaults,
}: ServiceExpansionRequestModalProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(defaults.name);
    setEmail(defaults.email);
    setPhone(defaults.phone);
    setState(defaults.state);
    setCity(defaults.city);
    setConsent(false);
  }, [isOpen, defaults]);

  console.log(
    "name",
    name,
    "email",
    email,
    "phone",
    phone,
    "state",
    state,
    "city",
    city,
    "consent",
    consent,
    "isSubmitting",
    isSubmitting
  );

  const canSubmit =
    name.trim() &&
    email.trim() &&
    // phone.trim() &&
    state.trim() &&
    city.trim() &&
    consent &&
    !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await propertyService.createServiceExpansionRequest({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || "",
        state: state.trim(),
        city: city.trim(),
        consent: true,
      });
      toastService.show("Thanks! We'll notify you when we launch in your area.", "success");
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      unconstrained
      className={styles.shell}
      bodyClassName={styles.shellModalBody}
      ariaLabel="Service availability"
    >
      <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
        ✕
      </button>
      <div className={styles.hero}>
        <div className={styles.pinWrap}>
          <MapPinHero />
        </div>
        <h2 id="sem-headline" className={styles.headline}>
          Currently Available in Texas Only
        </h2>
        <p className={styles.description}>
          We&apos;re currently offering our services exclusively in the state of Texas. Our team is
          actively working to expand to additional states very soon. If you&apos;d like to be
          notified when we launch in your area, please share your details below.
        </p>
      </div>

      <div className={styles.formBody}>
        <h3 className={styles.cardTitle}>Stay Updated When We Launch in Your State</h3>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="ser-name">
            Full Name
          </label>
          <div className={styles.inputRow}>
            <PersonIcon />
            <input
              id="ser-name"
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              placeholder="Full name"
            />
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="ser-phone">
            Phone Number
          </label>
          <div className={styles.inputRow}>
            <PhoneIcon />
            <input
              id="ser-phone"
              className={styles.input}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              inputMode="tel"
              placeholder="Enter your phone number"
            />
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="ser-email">
            Email Address
          </label>
          <div className={styles.inputRow}>
            <MailIcon />
            <input
              id="ser-email"
              className={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="Email address"
            />
          </div>
        </div>

        <div className={styles.row2}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="ser-state">
              State
            </label>
            <div className={styles.inputRow}>
              <PinIcon />
              <input
                id="ser-state"
                className={styles.input}
                value={state}
                onChange={(e) => setState(e.target.value)}
                autoComplete="address-level1"
                placeholder="State"
              />
            </div>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="ser-city">
              City
            </label>
            <div className={styles.inputRow}>
              <BuildingIcon />
              <input
                id="ser-city"
                className={styles.input}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                autoComplete="address-level2"
                placeholder="City"
              />
            </div>
          </div>
        </div>

        <label className={styles.consent}>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>I agree to be contacted when services become available in my area.</span>
        </label>

        <button
          type="button"
          className={styles.primaryBtn}
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          {isSubmitting ? "Submitting…" : "Notify Me When Available"}
        </button>
        <button type="button" className={styles.secondaryBtn} onClick={onClose}>
          ← Back to Home
        </button>
      </div>
    </Modal>
  );
}
